import { baseUrl } from "../../utils/global";
import { 
  Jeweler, 
  JewelerInput, 
  PosType, 
  Invoice, 
  InvoiceStatus,
  ReportResponse,
  MonthlyAnalysisResponse,
  ProductAnalysisResponse,
  UserReportResponse
} from "../../utils/types";
import { handleResponse } from "../ResponseHandler";

export const createJeweler = async (jeweler: JewelerInput) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${baseUrl}/jewelers`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      ...jeweler,
      status: 'ACTIVE'
    })
  });

  return handleResponse(response);
}

export const getJeweler = async (id: string) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${baseUrl}/jewelers/${id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  return handleResponse(response);
}

export const updateJeweler = async (id: string, jeweler: Partial<Jeweler>) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${baseUrl}/jewelers/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(jeweler)
  });

  return handleResponse(response);
}


export const setComissionRate = async (jewelerIds: string[], pos: PosType) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${baseUrl}/jewelers/bulk`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      ids: jewelerIds,
      pos
    })
  });

  return handleResponse(response);
}

export const updateBaseCommissionRate = async (key: string, value: number) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${baseUrl}/configuration/key/${key}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      value: value.toString()
    })
  });

  return handleResponse(response);
}

// Invoice Services
export const getInvoices = async (skip: number = 0, take: number = 10, search: string = '', status?: InvoiceStatus) => {
  const token = localStorage.getItem('token');
  const statusParam = status ? `&status=${status}` : '';
  const response = await fetch(`${baseUrl}/invoices?skip=${skip}&take=${take}&search=${search}${statusParam}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  return handleResponse(response);
}

export const getInvoice = async (id: string) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${baseUrl}/invoices/${id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  return handleResponse(response);
}

export const updateInvoiceStatus = async (id: string, status: InvoiceStatus) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${baseUrl}/invoices/${id}/status`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ status })
  });

  return handleResponse(response);
}

export const downloadInvoice = async (id: string) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${baseUrl}/invoices/${id}/download`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (response.ok) {
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${id}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  return handleResponse(response);
}

// Detailed Report Services
export const getDetailedReports = async (filters: any) => {
  const token = localStorage.getItem('token');
  const queryParams = new URLSearchParams();
  
  if (filters.startDate) queryParams.append('startDate', filters.startDate);
  if (filters.endDate) queryParams.append('endDate', filters.endDate);
  if (filters.jewelerId) queryParams.append('jewelerId', filters.jewelerId);
  if (filters.branchId) queryParams.append('branchId', filters.branchId);
  if (filters.reportType) queryParams.append('reportType', filters.reportType);

  const response = await fetch(`${baseUrl}/reports/detailed?${queryParams.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  return handleResponse(response);
}

export const getMonthlyReports = async (year?: number, month?: number) => {
  const token = localStorage.getItem('token');
  const queryParams = new URLSearchParams();
  
  if (year) queryParams.append('year', year.toString());
  if (month) queryParams.append('month', month.toString());

  const response = await fetch(`${baseUrl}/reports/monthly?${queryParams.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  return handleResponse(response);
}

export const getJewelerReports = async (jewelerId?: string, startDate?: string, endDate?: string) => {
  const token = localStorage.getItem('token');
  const queryParams = new URLSearchParams();
  
  if (jewelerId) queryParams.append('jewelerId', jewelerId);
  if (startDate) queryParams.append('startDate', startDate);
  if (endDate) queryParams.append('endDate', endDate);

  const response = await fetch(`${baseUrl}/reports/jeweler?${queryParams.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  return handleResponse(response);
}

export const getBranchReports = async (branchId?: string, startDate?: string, endDate?: string) => {
  const token = localStorage.getItem('token');
  const queryParams = new URLSearchParams();
  
  if (branchId) queryParams.append('branchId', branchId);
  if (startDate) queryParams.append('startDate', startDate);
  if (endDate) queryParams.append('endDate', endDate);

  const response = await fetch(`${baseUrl}/reports/branch?${queryParams.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  return handleResponse(response);
}

export const exportReportToExcel = async (filters: any, reportType: string) => {
  const token = localStorage.getItem('token');
  const queryParams = new URLSearchParams();
  
  if (filters.startDate) queryParams.append('startDate', filters.startDate);
  if (filters.endDate) queryParams.append('endDate', filters.endDate);
  if (filters.jewelerId) queryParams.append('jewelerId', filters.jewelerId);
  if (filters.branchId) queryParams.append('branchId', filters.branchId);
  queryParams.append('reportType', reportType);

  const response = await fetch(`${baseUrl}/reports/export/excel?${queryParams.toString()}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (response.ok) {
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rapor-${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  return handleResponse(response);
}

// New API Report Services
export const getReports = async (params?: {
  startDate?: string;
  endDate?: string;
  period?: 'monthly' | 'quarterly' | 'yearly';
  periodOffset?: number;
  jewelerId?: string;
  branchId?: string;
}): Promise<ReportResponse> => {
  const token = localStorage.getItem('token');
  const queryParams = new URLSearchParams();
  
  if (params?.startDate) queryParams.append('startDate', params.startDate);
  if (params?.endDate) queryParams.append('endDate', params.endDate);
  if (params?.period) queryParams.append('period', params.period);
  if (params?.periodOffset !== undefined) queryParams.append('periodOffset', params.periodOffset.toString());
  if (params?.jewelerId) queryParams.append('jewelerId', params.jewelerId);
  if (params?.branchId) queryParams.append('branchId', params.branchId);

  const response = await fetch(`${baseUrl}/reports?${queryParams.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  return handleResponse(response);
};

export const getMonthlyAnalysis = async (params?: {
  year?: number;
  months?: number;
  jewelerId?: string;
  branchId?: string;
}): Promise<MonthlyAnalysisResponse> => {
  const token = localStorage.getItem('token');
  const queryParams = new URLSearchParams();
  
  if (params?.year) queryParams.append('year', params.year.toString());
  if (params?.months) queryParams.append('months', params.months.toString());
  if (params?.jewelerId) queryParams.append('jewelerId', params.jewelerId);
  if (params?.branchId) queryParams.append('branchId', params.branchId);

  const response = await fetch(`${baseUrl}/reports/monthly-analysis?${queryParams.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  return handleResponse(response);
};

export const getProductAnalysis = async (params?: {
  startDate?: string;
  endDate?: string;
  period?: 'monthly' | 'quarterly' | 'yearly';
  periodOffset?: number;
  jewelerId?: string;
  branchId?: string;
}): Promise<ProductAnalysisResponse> => {
  const token = localStorage.getItem('token');
  const queryParams = new URLSearchParams();
  
  if (params?.startDate) queryParams.append('startDate', params.startDate);
  if (params?.endDate) queryParams.append('endDate', params.endDate);
  if (params?.period) queryParams.append('period', params.period);
  if (params?.periodOffset !== undefined) queryParams.append('periodOffset', params.periodOffset.toString());
  if (params?.jewelerId) queryParams.append('jewelerId', params.jewelerId);
  if (params?.branchId) queryParams.append('branchId', params.branchId);

  const response = await fetch(`${baseUrl}/reports/product-analysis?${queryParams.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  return handleResponse(response);
};

export const getUserReports = async (params?: {
  userId?: string;
  startDate?: string;
  endDate?: string;
  period?: 'monthly' | 'quarterly' | 'yearly';
  periodOffset?: number;
  jewelerId?: string;
  branchId?: string;
}): Promise<UserReportResponse> => {
  const token = localStorage.getItem('token');
  const queryParams = new URLSearchParams();
  
  if (params?.userId) queryParams.append('userId', params.userId);
  if (params?.startDate) queryParams.append('startDate', params.startDate);
  if (params?.endDate) queryParams.append('endDate', params.endDate);
  if (params?.period) queryParams.append('period', params.period);
  if (params?.periodOffset !== undefined) queryParams.append('periodOffset', params.periodOffset.toString());
  if (params?.jewelerId) queryParams.append('jewelerId', params.jewelerId);
  if (params?.branchId) queryParams.append('branchId', params.branchId);

  const response = await fetch(`${baseUrl}/reports/user-reports?${queryParams.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  return handleResponse(response);
};
